import {
  BufferGeometry,
  Material,
  Mesh,
  Raycaster,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";

import { ArtworkDialog } from "./artwork-dialog";
import { loadMuseumAssets, type LoadPhase, type LoadedAssets } from "./asset-loader";
import type { MuseumContent, PublicExhibition } from "./contracts";
import {
  copyFor,
  localizeContent,
  readLanguage,
  storeLanguage,
  type Language,
  type UiCopy,
} from "./i18n";
import { selectArtwork, type ArtworkSelection } from "./interaction";
import { MuseumMiniMap } from "./minimap";
import {
  artworkTextureImage,
  createRenderer,
  currentRoom,
  parseMuseumScene,
  type MuseumScene,
} from "./museum-scene";
import { FirstPersonController } from "./navigation";
import { RouteManager } from "./route";
import {
  createViewerUI,
  populateNavigationPresets,
  populateRouteList,
  type ViewerUI,
} from "./ui";

type ViewerPhase = "loading" | "ready" | "entered" | "paused" | "error";

export class MuseumApp {
  readonly #language: Language;
  readonly #copy: UiCopy;
  readonly #ui: ViewerUI;
  readonly #artworkDialog: ArtworkDialog;
  readonly #exhibition: PublicExhibition;
  readonly #onBack: () => void;
  readonly #abort = new AbortController();
  #phase: ViewerPhase = "loading";
  #assets: LoadedAssets | null = null;
  #museum: MuseumScene | null = null;
  #renderer: WebGLRenderer | null = null;
  #controller: FirstPersonController | null = null;
  #route: RouteManager | null = null;
  #miniMap: MuseumMiniMap | null = null;
  #selection: ArtworkSelection | null = null;
  #lastSelectionId = "";
  #disposed = false;
  #lastRoom = "";
  #roomTitles: Record<string, string> = {};
  #lastTime = performance.now();
  #frame = 0;
  #entered = false;
  #contextLost = false;
  readonly #raycaster = new Raycaster();
  readonly #touchPointer = new Vector2(0, 0);

  constructor(root: HTMLElement, exhibition: PublicExhibition, onBack: () => void) {
    this.#exhibition = exhibition;
    this.#onBack = onBack;
    this.#language = readLanguage();
    this.#copy = copyFor(this.#language);
    document.documentElement.lang = this.#copy.locale;
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute(
      "content",
      this.#copy.pageDescription,
    );
    this.#ui = createViewerUI(root, this.#language, this.#copy);
    this.#artworkDialog = new ArtworkDialog(
      this.#ui.artworkDialog,
      this.#copy,
      () => this.#resumeAfterUiAction(),
    );
    this.#bindShell();
  }

  async start(): Promise<void> {
    try {
      const loaded = await loadMuseumAssets(
        this.#exhibition,
        (phase) => this.#setLoadPhase(phase),
        this.#language,
      );
      this.#assets = { ...loaded, content: localizeContent(loaded.content, loaded.localization) };
      this.#populateContent(this.#assets.content);
      if (!supportsWebGL2()) {
        this.#ui.fatalRoute.hidden = false;
        throw new Error("WEBGL2_UNAVAILABLE");
      }
      this.#museum = await parseMuseumScene(
        this.#assets.glb,
        this.#assets.assetBaseUrl,
        this.#assets.content,
      );
      this.#renderer = createRenderer(this.#ui.canvas);
      this.#controller = new FirstPersonController(this.#museum.camera, this.#museum.colliders);
      this.#route = new RouteManager(
        this.#assets.content,
        this.#museum.routeStops,
        this.#museum.passage,
      );
      this.#miniMap = new MuseumMiniMap(
        this.#ui.minimapCanvas,
        this.#museum,
        this.#assets.content,
        (artworkId) => this.#navigateToArtwork(artworkId),
      );
      this.#bindRuntime();
      this.#resize();
      this.#phase = "ready";
      this.#ui.loadStatus.textContent = this.#copy.ready(
        this.#assets.content.rooms.length,
        this.#assets.content.artworks.length,
      );
      this.#ui.enter.disabled = false;
      this.#openFragmentArtwork();
      this.#installDiagnostics();
      this.#frame = requestAnimationFrame((time) => this.#animate(time));
    } catch (error) {
      this.#fail(error);
    }
  }

  #bindShell(): void {
    this.#ui.backButtons.forEach((button) => button.addEventListener("click", () => this.#back()));
    this.#ui.languageSelectors.forEach((selector) => {
      selector.addEventListener("change", () => {
        const language = selector.value === "en" ? "en" : "es";
        storeLanguage(language);
        window.location.reload();
      });
    });
    this.#ui.enter.addEventListener("click", () => this.#enter());
    this.#ui.continueButton.addEventListener("click", () => this.#resume());
    this.#ui.retry.addEventListener("click", () => window.location.reload());
    this.#ui.routeButtons.forEach((button) =>
      button.addEventListener("click", () => this.#openOverlay(this.#ui.routeDialog)),
    );
    this.#ui.helpButtons.forEach((button) =>
      button.addEventListener("click", () => this.#openOverlay(this.#ui.helpDialog)),
    );
    this.#ui.root
      .querySelector<HTMLElement>("[data-close-route]")
      ?.addEventListener("click", () => {
        this.#ui.routeDialog.close();
        this.#resumeAfterUiAction();
      });
    this.#ui.root
      .querySelector<HTMLElement>("[data-close-help]")
      ?.addEventListener("click", () => {
        this.#ui.helpDialog.close();
        this.#resumeAfterUiAction();
      });
    for (const dialog of [
      this.#ui.routeDialog,
      this.#ui.helpDialog,
      this.#ui.artworkDialog,
    ]) {
      dialog.addEventListener("cancel", () => {
        window.setTimeout(() => this.#resumeAfterUiAction(), 0);
      });
    }
    this.#ui.artworkDialog.addEventListener("close", () => {
      if (window.location.hash.startsWith("#artwork=")) {
        history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
      }
    });
    window.addEventListener("hashchange", () => this.#openFragmentArtwork(), {
      signal: this.#abort.signal,
    });
  }

  #bindRuntime(): void {
    const controller = this.#requiredController();
    this.#ui.previous.addEventListener("click", () => {
      this.#requiredRoute().previous();
      this.#updateRouteUI(true);
    });
    this.#ui.next.addEventListener("click", () => {
      this.#requiredRoute().next();
      this.#updateRouteUI(true);
    });
    this.#ui.reset.addEventListener("click", () => this.#reset());
    this.#ui.navigationPreset.addEventListener("change", () => {
      const artworkId = this.#ui.navigationPreset.value;
      if (artworkId !== "") this.#navigateToArtwork(artworkId);
    });
    this.#ui.touchInteract.addEventListener("click", () => this.#openSelection(this.#ui.touchInteract));
    document.addEventListener("keydown", (event) => this.#key(event, true), {
      signal: this.#abort.signal,
    });
    document.addEventListener("keyup", (event) => this.#key(event, false), {
      signal: this.#abort.signal,
    });
    document.addEventListener("mousemove", (event) => {
      if (document.pointerLockElement === this.#ui.canvas) controller.look(event.movementX, event.movementY);
    }, { signal: this.#abort.signal });
    document.addEventListener("pointerlockchange", () => {
      const locked = document.pointerLockElement === this.#ui.canvas;
      controller.setEnabled(locked || (this.#ui.isTouch && this.#phase === "entered"));
      if (!locked && this.#entered && !this.#ui.isTouch && !this.#overlayOpen()) this.#showPause();
    }, { signal: this.#abort.signal });
    this.#ui.canvas.addEventListener("click", () => {
      if (document.pointerLockElement === this.#ui.canvas) this.#openSelection(this.#ui.canvas);
    });
    this.#ui.canvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      this.#contextLost = true;
      controller.setEnabled(false);
      cancelAnimationFrame(this.#frame);
      this.#fail(new Error("WEBGL_CONTEXT_LOST"));
    });
    window.addEventListener("resize", () => this.#resize(), { signal: this.#abort.signal });
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) controller.setEnabled(false);
      else {
        this.#lastTime = performance.now();
        if (this.#ui.isTouch && this.#phase === "entered" && !this.#overlayOpen()) {
          controller.setEnabled(true);
        }
      }
    }, { signal: this.#abort.signal });
    this.#bindTouch(controller);
  }

  #bindTouch(controller: FirstPersonController): void {
    let joystickPointer: number | null = null;
    let lookPointer: number | null = null;
    let lookX = 0;
    let lookY = 0;
    let lookTravel = 0;
    this.#ui.joystick.addEventListener("pointerdown", (event) => {
      joystickPointer = event.pointerId;
      if (event.isTrusted) this.#ui.joystick.setPointerCapture(event.pointerId);
    });
    this.#ui.joystick.addEventListener("pointermove", (event) => {
      if (event.pointerId !== joystickPointer) return;
      const bounds = this.#ui.joystick.getBoundingClientRect();
      const x = Math.max(-1, Math.min(1, (event.clientX - (bounds.left + bounds.width / 2)) / 45));
      const y = Math.max(-1, Math.min(1, (event.clientY - (bounds.top + bounds.height / 2)) / 45));
      controller.setTouchMovement(x, -y);
      this.#ui.joystickKnob.style.transform = `translate(${x * 28}px, ${y * 28}px)`;
    });
    const clearJoystick = (event: PointerEvent): void => {
      if (event.pointerId !== joystickPointer) return;
      joystickPointer = null;
      controller.setTouchMovement(0, 0);
      this.#ui.joystickKnob.style.transform = "translate(0, 0)";
    };
    this.#ui.joystick.addEventListener("pointerup", clearJoystick);
    this.#ui.joystick.addEventListener("pointercancel", clearJoystick);
    this.#ui.canvas.addEventListener("pointerdown", (event) => {
      if (!this.#ui.isTouch || this.#phase !== "entered") return;
      this.#setTouchPointer(event);
      lookPointer = event.pointerId;
      lookX = event.clientX;
      lookY = event.clientY;
      lookTravel = 0;
      if (event.isTrusted) this.#ui.canvas.setPointerCapture(event.pointerId);
    });
    this.#ui.canvas.addEventListener("pointermove", (event) => {
      if (event.pointerId !== lookPointer) return;
      this.#setTouchPointer(event);
      const movementX = event.clientX - lookX;
      const movementY = event.clientY - lookY;
      lookTravel += Math.hypot(movementX, movementY);
      controller.look(movementX, movementY);
      lookX = event.clientX;
      lookY = event.clientY;
    });
    const clearLook = (event: PointerEvent): void => {
      if (event.pointerId !== lookPointer) return;
      if (event.type === "pointerup" && lookTravel < 8) {
        this.#setTouchPointer(event);
        const selection = this.#selectionAt(this.#touchPointer);
        if (selection !== null) {
          const artworkId = selection.artworkId;
          // Let the browser finish the touch/click sequence before inserting
          // the modal, otherwise the trailing click can hit the new dialog.
          window.setTimeout(() => this.#openArtwork(artworkId, this.#ui.canvas, true), 0);
        }
      }
      lookPointer = null;
    };
    this.#ui.canvas.addEventListener("pointerup", clearLook);
    this.#ui.canvas.addEventListener("pointercancel", clearLook);
  }

  #key(event: KeyboardEvent, pressed: boolean): void {
    if (isEditable(event.target)) return;
    const controller = this.#controller;
    if (controller === null) return;
    if (["KeyW", "KeyA", "KeyS", "KeyD", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.code)) {
      event.preventDefault();
      controller.setKey(event.code, pressed);
      return;
    }
    if (!pressed || event.repeat) return;
    if (event.code === "KeyE") this.#openSelection(this.#ui.canvas);
    else if (/^Digit[1-9]$/u.test(event.code)) {
      event.preventDefault();
      const index = Number(event.code.slice(-1)) - 1;
      const artwork = this.#assets?.content.artworks[index];
      if (artwork !== undefined) this.#navigateToArtwork(artwork.id);
    } else if (event.code === "KeyM") this.#ui.minimap.open = !this.#ui.minimap.open;
    else if (event.code === "BracketLeft") {
      this.#requiredRoute().previous();
      this.#updateRouteUI(true);
    } else if (event.code === "BracketRight") {
      this.#requiredRoute().next();
      this.#updateRouteUI(true);
    } else if (event.code === "KeyO") this.#openOverlay(this.#ui.routeDialog);
    else if (event.code === "KeyR") this.#reset();
    else if (event.code === "Slash" && event.shiftKey) this.#openOverlay(this.#ui.helpDialog);
  }

  #enter(): void {
    if (this.#phase !== "ready") return;
    this.#entered = true;
    this.#phase = "entered";
    this.#ui.welcome.hidden = true;
    this.#ui.pause.hidden = true;
    this.#ui.hud.hidden = false;
    this.#ui.reticle.hidden = this.#ui.isTouch;
    this.#ui.touchControls.hidden = !this.#ui.isTouch;
    this.#ui.minimap.hidden = false;
    this.#requiredController().setEnabled(this.#ui.isTouch);
    if (!this.#ui.isTouch) void this.#requestPointerLock();
    this.#announce(this.#copy.visitStarted(this.#requiredRoute().active.title));
  }

  #resume(): void {
    if (!this.#entered || this.#phase === "error") return;
    this.#phase = "entered";
    this.#ui.pause.hidden = true;
    const hasControl = this.#ui.isTouch || document.pointerLockElement === this.#ui.canvas;
    this.#requiredController().setEnabled(hasControl);
    if (!hasControl) void this.#requestPointerLock();
  }

  #resumeAfterUiAction(): void {
    if (this.#entered && !this.#overlayOpen()) this.#resume();
  }

  async #requestPointerLock(): Promise<void> {
    try {
      await this.#ui.canvas.requestPointerLock();
    } catch {
      this.#showPause(this.#copy.pointerLockFailure);
    }
  }

  #showPause(message?: string): void {
    if (!this.#entered || this.#phase === "error") return;
    this.#phase = "paused";
    this.#requiredController().setEnabled(false);
    this.#ui.pause.hidden = false;
    const paragraph = this.#ui.pause.querySelector("p:not(.eyebrow)");
    if (paragraph !== null) {
      paragraph.textContent =
        message ?? this.#copy.defaultPause;
    }
  }

  #openOverlay(dialog: HTMLDialogElement): void {
    this.#pauseForOverlay();
    if (!dialog.open) dialog.showModal();
  }

  #pauseForOverlay(): void {
    this.#controller?.setEnabled(false);
    if (document.pointerLockElement !== null) document.exitPointerLock();
  }

  #openSelection(invoker: HTMLElement): void {
    if (this.#selection !== null) this.#openArtwork(this.#selection.artworkId, invoker, true);
  }

  #openArtwork(artworkId: string, invoker: HTMLElement, updateFragment: boolean): void {
    const assets = this.#assets;
    const museum = this.#museum;
    const route = this.#route;
    if (assets === null || museum === null || route === null) return;
    const content = assets.content;
    const artwork = content.artworks.find((candidate) => candidate.id === artworkId);
    const painting = museum.paintings.get(artworkId);
    if (artwork === undefined || painting === undefined) return;
    this.#pauseForOverlay();
    route.visit(artworkId);
    this.#ui.routeList
      .querySelector<HTMLButtonElement>(`button[data-artwork-id="${artworkId}"]`)
      ?.classList.add("visited");
    const roomTitle = content.rooms.find((room) => room.id === artwork.room_id)?.title ?? artwork.room_id;
    this.#artworkDialog.open(artwork, roomTitle, artworkTextureImage(painting), invoker);
    if (updateFragment) history.pushState(null, "", `#artwork=${artwork.id}`);
    this.#updateRouteUI(true);
  }

  #openFragmentArtwork(): void {
    if (this.#assets === null) return;
    const match = /^#artwork=([0-9a-f]{32})$/u.exec(window.location.hash);
    if (match === null) {
      if (window.location.hash !== "") {
        history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
        this.#announce(this.#copy.invalidLink);
      }
      if (this.#ui.artworkDialog.open) {
        this.#ui.artworkDialog.close();
        this.#resumeAfterUiAction();
      }
      return;
    }
    const id = match[1];
    if (id === undefined || !this.#assets.content.artworks.some((artwork) => artwork.id === id)) {
      history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
      this.#announce(this.#copy.unknownArtwork);
      return;
    }
    this.#openArtwork(id, this.#ui.routeButtons[0] ?? this.#ui.enter, false);
  }

  #populateContent(content: MuseumContent): void {
    document.title = `${content.title} — ${this.#language === "en" ? "Virtual Museum" : "Museo virtual"}`;
    this.#ui.exhibitionTitle.textContent = content.title;
    this.#ui.exhibitionThesis.textContent = content.thesis;
    this.#roomTitles = {
      corridor: this.#copy.corridor,
      ...Object.fromEntries(content.rooms.map((room) => [room.id, room.title])),
    };
    populateRouteList(this.#ui, content, new Set(), (id, button) =>
      this.#openArtwork(id, button, true),
    );
    populateNavigationPresets(this.#ui, content);
  }

  #reset(): void {
    this.#requiredController().reset();
    this.#ui.navigationPreset.value = "";
    this.#announce(this.#copy.resetAnnouncement);
  }

  #navigateToArtwork(artworkId: string): void {
    const museum = this.#museum;
    const content = this.#assets?.content;
    const route = this.#route;
    if (museum === null || content === undefined || route === null) return;
    const artwork = content.artworks.find((candidate) => candidate.id === artworkId);
    const position = museum.routeStops.get(artworkId);
    const painting = museum.paintings.get(artworkId);
    if (artwork === undefined || position === undefined || painting === undefined) return;
    this.#requiredController().navigateToArtwork(position, painting, museum.labels.get(artworkId));
    route.setActive(artwork.route_order);
    this.#selection = null;
    this.#lastSelectionId = "";
    this.#ui.navigationPreset.value = artworkId;
    this.#updateRouteUI(false);
    this.#announce(this.#copy.navigationPosition(artwork.title));
    this.#resumeAfterUiAction();
  }

  #animate(time: number): void {
    if (this.#disposed) return;
    if (this.#contextLost) return;
    const delta = Math.min((time - this.#lastTime) / 1000, 1 / 30);
    this.#lastTime = time;
    if (
      !document.hidden &&
      this.#renderer !== null &&
      this.#museum !== null &&
      !this.#overlayOpen()
    ) {
      this.#controller?.update(delta);
      this.#updateFrameState();
      this.#renderer.render(this.#museum.scene, this.#museum.camera);
    }
    this.#frame = requestAnimationFrame((next) => this.#animate(next));
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#abort.abort();
    cancelAnimationFrame(this.#frame);
    this.#controller?.setEnabled(false);
    if (document.pointerLockElement === this.#ui.canvas) document.exitPointerLock();
    for (const dialog of [this.#ui.routeDialog, this.#ui.helpDialog, this.#ui.artworkDialog]) {
      if (dialog.open) dialog.close();
    }
    this.#museum?.scene.traverse((object) => {
      if (!(object instanceof Mesh)) return;
      const mesh = object as unknown as {
        geometry: BufferGeometry;
        material: Material | Material[];
      };
      mesh.geometry.dispose();
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) {
        for (const value of Object.values(material as unknown as Record<string, unknown>)) {
          if (value instanceof Texture) value.dispose();
        }
        material.dispose();
      }
    });
    this.#renderer?.dispose();
    if (import.meta.env.VITE_E2E === "1") Reflect.deleteProperty(window, "__MUSEUM_DIAGNOSTICS__");
  }

  #back(): void {
    this.dispose();
    this.#onBack();
  }

  #updateFrameState(): void {
    const museum = this.#museum;
    const route = this.#route;
    if (museum === null || route === null || this.#phase === "error") return;
    this.#selection = this.#selectionAt(this.#ui.isTouch ? this.#touchPointer : undefined);
    const selectionId = this.#selection?.artworkId ?? "";
    if (selectionId !== this.#lastSelectionId) {
      this.#lastSelectionId = selectionId;
      this.#ui.selection.hidden = this.#selection === null;
      this.#ui.selection.textContent =
        this.#selection === null ? "" : this.#copy.selection(this.#selection.title);
      this.#ui.reticle.classList.toggle("active", this.#selection !== null);
      this.#ui.touchInteract.disabled = this.#selection === null;
      this.#ui.touchInteract.textContent =
        this.#selection === null ? this.#copy.viewArtwork : this.#selection.title;
    }
    const room = currentRoom(museum.camera.position, museum.roomFloors);
    if (room !== this.#lastRoom) {
      this.#lastRoom = room;
      this.#ui.room.textContent = this.#roomTitles[room] ?? room;
      this.#announce(this.#copy.roomChanged(this.#ui.room.textContent));
    }
    const guidance = route.guidance(museum.camera.position);
    this.#ui.guidanceLabel.textContent = guidance.viaPassage
      ? this.#copy.towardsPassage
      : this.#copy.towards(route.active.title);
    this.#ui.guidanceDistance.textContent = guidance.distance < 0.6 ? "" : `${guidance.distance.toFixed(1)} m`;
    const forward = new Vector3(0, 0, -1).applyQuaternion(museum.camera.quaternion);
    forward.y = 0;
    forward.normalize();
    const angle = Math.atan2(
      forward.x * guidance.direction.z - forward.z * guidance.direction.x,
      forward.dot(guidance.direction),
    );
    this.#ui.guidanceArrow.style.transform = `rotate(${angle}rad)`;
    this.#miniMap?.render(route.active.artworkId);
  }

  #selectionAt(pointer?: Vector2): ArtworkSelection | null {
    const museum = this.#museum;
    if (museum === null) return null;
    return selectArtwork(this.#raycaster, museum.camera, museum.scene, pointer);
  }

  #updateRouteUI(announce: boolean): void {
    const route = this.#requiredRoute();
    this.#ui.routeTitle.textContent = route.completed ? this.#copy.routeCompleted : route.active.title;
    this.#ui.routeProgress.textContent = `${route.activeOrder}/${route.targets.length}`;
    this.#ui.previous.disabled = route.activeOrder === 1;
    this.#ui.next.disabled = route.activeOrder === route.targets.length;
    if (announce) {
      this.#announce(
        route.completed
          ? this.#copy.completedWith(route.active.title)
          : this.#copy.activeArtwork(route.activeOrder, route.targets.length, route.active.title),
      );
    }
  }

  #resize(): void {
    if (this.#renderer === null || this.#museum === null) return;
    const width = this.#ui.canvas.clientWidth;
    const height = this.#ui.canvas.clientHeight;
    this.#renderer.setSize(width, height, false);
    this.#museum.camera.aspect = width / Math.max(height, 1);
    this.#museum.camera.updateProjectionMatrix();
  }

  #setTouchPointer(event: PointerEvent): void {
    const bounds = this.#ui.canvas.getBoundingClientRect();
    this.#touchPointer.set(
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
      -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
    );
  }

  #setLoadPhase(phase: LoadPhase): void {
    this.#ui.loadStatus.textContent = this.#copy.loadPhases[phase];
  }

  #fail(error: unknown): void {
    this.#phase = "error";
    this.#controller?.setEnabled(false);
    this.#ui.welcome.hidden = true;
    this.#ui.pause.hidden = true;
    this.#ui.hud.hidden = true;
    this.#ui.touchControls.hidden = true;
    this.#ui.minimap.hidden = true;
    this.#ui.fatal.hidden = false;
    this.#ui.fatalMessage.textContent = friendlyError(error, this.#copy);
    this.#announce(this.#ui.fatalMessage.textContent);
  }

  #announce(message: string): void {
    this.#ui.announcer.textContent = "";
    window.setTimeout(() => {
      this.#ui.announcer.textContent = message;
    }, 10);
  }

  #overlayOpen(): boolean {
    return [this.#ui.artworkDialog, this.#ui.routeDialog, this.#ui.helpDialog].some(
      (dialog) => dialog.open,
    );
  }

  #requiredController(): FirstPersonController {
    if (this.#controller === null) throw new Error("VIEWER_NOT_READY");
    return this.#controller;
  }

  #requiredRoute(): RouteManager {
    if (this.#route === null) throw new Error("VIEWER_NOT_READY");
    return this.#route;
  }

  #installDiagnostics(): void {
    if (import.meta.env.VITE_E2E !== "1" || this.#museum === null) return;
    const museum = this.#museum;
    const canvas = this.#ui.canvas;
    Object.defineProperty(window, "__MUSEUM_DIAGNOSTICS__", {
      value: Object.freeze({
        counts: Object.freeze({
          paintings: museum.paintings.size,
          labels: museum.labels.size,
          routeStops: museum.routeStops.size,
          colliders: museum.colliders.length,
          roomFills: museum.roomFillCount,
        }),
        get phase() {
          return document.querySelector("[data-fatal]:not([hidden])") === null ? "ready" : "error";
        },
        get cameraPosition() {
          return museum.camera.position.toArray();
        },
        get cameraForward() {
          return new Vector3(0, 0, -1).applyQuaternion(museum.camera.quaternion).toArray();
        },
        labelScreenPosition(artworkId: string) {
          const label = museum.labels.get(artworkId);
          if (label === undefined) return null;
          const projected = label.getWorldPosition(new Vector3()).project(museum.camera);
          const bounds = canvas.getBoundingClientRect();
          return [
            bounds.left + ((projected.x + 1) / 2) * bounds.width,
            bounds.top + ((1 - projected.y) / 2) * bounds.height,
          ];
        },
      }),
      writable: false,
      configurable: true,
    });
  }
}

function supportsWebGL2(): boolean {
  try {
    return document.createElement("canvas").getContext("webgl2") !== null;
  } catch {
    return false;
  }
}

function isEditable(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function friendlyError(error: unknown, copy: UiCopy): string {
  const message = error instanceof Error ? error.message : "";
  if (message.includes("WEBGL2_UNAVAILABLE")) {
    return copy.webglFailure;
  }
  if (message.includes("WEBGL_CONTEXT_LOST")) {
    return copy.contextLoss;
  }
  if (message.includes("HASH") || message.includes("IDENTITY") || message.includes("CONTRACT")) {
    return copy.identityFailure;
  }
  if (message.includes("HTTP")) {
    return copy.missingAsset;
  }
  return copy.genericFailure;
}

declare global {
  // Window must be an interface to augment the browser global.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    __MUSEUM_DIAGNOSTICS__?: Readonly<{
      counts: Readonly<{
        paintings: number;
        labels: number;
        routeStops: number;
        colliders: number;
        roomFills: number;
      }>;
      readonly phase: string;
      readonly cameraPosition: number[];
      readonly cameraForward: number[];
      readonly labelScreenPosition: (artworkId: string) => number[] | null;
    }>;
  }
}
